import { Chip, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { userService } from '../../../api/userService';
import { Field, FieldGrid, SectionCard } from '../../../components/Section';
import { TreasuryStep } from '../../../constants/treasury';
import { useAppDispatch } from '../../../redux/hooks';
import { fetchPortfolios } from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.actions';
import { companyGroupSelector } from '../../../redux/modules/b2bThriftPlan/companyGroups/companyGroup.selectors';
import { TreasuryCompanySetup, TreasuryKyb } from '../../../types/treasury';
import { User } from '../../../types/user';
import { PortfolioCards } from '../CompanyGroups/components/PortfolioCards';
import { TreasuryOwnerAgreementSection } from './components/TreasuryOwnerAgreementSection';

type TreasuryStepSummaryProps = {
  step: TreasuryStep;
  setup: TreasuryCompanySetup;
  kyb?: TreasuryKyb | null;
  /** When the owner's KYC was confirmed (Confirm KYC step). */
  kycConfirmDate?: string | null;
};

const LockedChip = () => <Chip size="small" variant="outlined" label="Can't be changed" />;

/**
 * A finished step the backend doesn't let you change, shown with its real data. The treasury group, department and
 * product are set at setup only, the treasury employee can't be edited through any route (P3.7), the personal email
 * changes only once, and the first agreements acceptance stays. The steps that CAN change again — the company KYB and
 * "company verified" — open as their forms instead (CompanyTreasury).
 */
export const TreasuryStepSummary = ({ step, setup, kyb, kycConfirmDate }: TreasuryStepSummaryProps) => {
  const dispatch = useAppDispatch();
  const { portfolios } = useSelector(companyGroupSelector);
  const [account, setAccount] = useState<User | null>(null);
  const [isAccountLoading, setIsAccountLoading] = useState(false);
  const ownerUserId = setup.employee?.external_user_id;

  // The group step shows the product among the cards.
  useEffect(() => {
    if (step === TreasuryStep.GroupAndDepartment) {
      dispatch(fetchPortfolios({}));
    }
  }, [step, dispatch]);

  // The personal email step shows the account's real email, read from madkhol-user.
  useEffect(() => {
    if (step !== TreasuryStep.PersonalEmail || !ownerUserId) {
      return;
    }

    setIsAccountLoading(true);
    userService
      .fetchUser(ownerUserId)
      .then((response) => setAccount(response.data ?? null))
      .catch(() => setAccount(null))
      .finally(() => setIsAccountLoading(false));
  }, [step, ownerUserId]);

  if (step === TreasuryStep.GroupAndDepartment) {
    return (
      <Stack spacing={2}>
        <SectionCard title="Treasury Group" description="Set at setup; not editable." action={<LockedChip />}>
          <FieldGrid>
            <Field label="Name" value={setup.group.name} />
          </FieldGrid>
        </SectionCard>
        <SectionCard title="Product" description="The group's one product.">
          <PortfolioCards
            portfolios={portfolios ?? []}
            selectedPortfolioId={setup.group.portfolioGroups?.[0]?.external_portfolio_id}
            onSelect={() => undefined}
          />
        </SectionCard>
        <SectionCard title="Treasury Department" action={<LockedChip />}>
          <FieldGrid>
            <Field label="Name" value={setup.department.name} />
            <Field label="Description" value={setup.department.description} />
          </FieldGrid>
        </SectionCard>
      </Stack>
    );
  }

  if (step === TreasuryStep.TreasuryEmployee && setup.employee) {
    return (
      <SectionCard title="Treasury Employee" description="The company owner." action={<LockedChip />}>
        <FieldGrid>
          <Field label="Full Name (English)" value={setup.employee.full_name_en} />
          <Field label="Full Name (Arabic)" value={setup.employee.full_name_ar} />
          <Field label="Work Email" value={setup.employee.email} />
          <Field label="Phone Number" value={setup.employee.phone_number} />
          <Field label="National ID" value={setup.employee.national_id} />
          <Field label="Status" value={setup.employee.status} />
        </FieldGrid>
      </SectionCard>
    );
  }

  if (step === TreasuryStep.PersonalEmail) {
    return (
      <SectionCard
        title="Emails"
        description="The personal email replaces the account email only once, while it still equals the work email."
      >
        <FieldGrid>
          <Field label="Work Email" value={setup.employee?.email} />
          <Field
            label="Account Email (now)"
            value={isAccountLoading ? 'Loading…' : account?.email ?? 'Could not be read'}
          />
        </FieldGrid>
      </SectionCard>
    );
  }

  if (step === TreasuryStep.ApproveEmployee) {
    return (
      <SectionCard
        title="Approved"
        description="The treasury employee is approved and the treasury is active."
        action={<Chip size="small" color="success" label={setup.status} />}
      >
        <FieldGrid>
          <Field label="Full Name (English)" value={setup.employee?.full_name_en} />
          <Field label="Full Name (Arabic)" value={setup.employee?.full_name_ar} />
          <Field label="Work Email" value={setup.employee?.email} />
          <Field label="National ID" value={setup.employee?.national_id} />
          <Field label="Employee Status" value={setup.employee?.status} />
        </FieldGrid>
      </SectionCard>
    );
  }

  if (step === TreasuryStep.ConfirmKyc && kycConfirmDate) {
    return (
      <SectionCard
        title="KYC Confirmed"
        description="The owner's KYC is confirmed. The date stays as it was first set."
        action={<Chip size="small" color="success" label="Confirmed" />}
      >
        <FieldGrid>
          <Field label="Full Name (English)" value={setup.employee?.full_name_en} />
          <Field label="Confirmed At" value={new Date(kycConfirmDate).toLocaleString()} />
        </FieldGrid>
      </SectionCard>
    );
  }

  const agreements = kyb?.agreements ?? setup.agreements;

  if (step === TreasuryStep.AlpacaAgreements && agreements) {
    return (
      <Stack spacing={2}>
        {/* The owner's own agreement stays signable here, after the company agreements are accepted. */}
        <TreasuryOwnerAgreementSection setup={setup} />
        <SectionCard
          title="Company Agreements"
          description="Both Alpaca agreements are accepted. The first acceptance stays."
          action={<Chip size="small" color="success" label="Accepted" />}
        >
          <FieldGrid columns={3}>
            <Field label="Accepted By" value={agreements.acceptedByName} />
            <Field
              label="Accepted As"
              value={agreements.acceptedByType === 'MADKHOL_ADMIN' ? 'Madkhol admin' : 'Company admin'}
            />
            <Field label="Accepted At" value={new Date(agreements.acceptedAt).toLocaleString()} />
          </FieldGrid>
        </SectionCard>
      </Stack>
    );
  }

  return null;
};
